import {add, isAfter, isBefore, isEqual, startOfDay, sub} from "date-fns";
import {Voyage} from "views/ProcessDetails/refactor/types/voyages.types";

import {
  ID_COLLECT_VOYAGE,
  ID_DELIVERY_VOYAGE,
  ID_FINAL_DESTINATION_VOYAGE,
  ID_INITIAL_VOYAGE,
  ID_RECEIVING_LOCATION,
} from "views/ProcessDetails/utils/constants";
import {utcToZonedTime} from "date-fns-tz";
import {brazilianTimeZoneCurrentDate, timeZone} from "global/constants";
import {useFormContext} from "react-hook-form";
import {Process} from "../refactor/types/process.types";

/**
 * Quando a tela de processos antigos for removida, alterar a tipagem de voyage para a nova.
 */
const useValidateVoyages = () => {
  const {watch} = useFormContext<Process>();
  const isAiProcess =
    `${watch("operation")?.[0]}${watch("modality")?.[0]}` === "IA";

  const shouldDisableLoad = (currentVoyageIndex: number, voyages: Voyage[]) => {
    /**
     * [] Apenas habilitar caso anterior tenha desembarcado (exceto coleta)
     * [] Desabilitar caso atual tenha desembarcado
     */

    //Não aplicar bloqueio para coleta, DTA e entrega
    if (
      ![
        ID_COLLECT_VOYAGE,
        ID_DELIVERY_VOYAGE,
        ID_FINAL_DESTINATION_VOYAGE,
        ID_RECEIVING_LOCATION,
      ].includes(voyages[currentVoyageIndex]?.typeVoyage?.id ?? 0)
    ) {
      if (voyages[currentVoyageIndex]?.cargoLoad === "") {
        return {disabled: true, message: "Embarcar via atividade"};
      }
    }

    //load da primeira viagem sempre habilitado, exceto se já tiver desembarcado
    if (currentVoyageIndex === 0 && !voyages[currentVoyageIndex]?.cargoUnload) {
      return {disabled: false};
    }

    if (
      voyages[currentVoyageIndex]?.cargoUnload &&
      voyages[currentVoyageIndex]?.cargoLoad
    ) {
      return {disabled: true, message: "Viagem desembarcada"};
    }

    const previousVoyage = voyages[currentVoyageIndex - 1];

    //se for coleta, o Load vai estar sempre habilitado

    //não desabilita campos se for coleta, mas ela não pode ter datas de load e Unload
    //caso tenha uma das datas preenchidas, se comporta como qualquer outro tipo
    //de viagem
    if (
      previousVoyage?.typeVoyage?.id === ID_COLLECT_VOYAGE &&
      !previousVoyage?.cargoLoad &&
      !previousVoyage?.cargoUnload
    ) {
      return {disabled: false};
    }

    if (
      !previousVoyage?.cargoUnload &&
      voyages[currentVoyageIndex]?.typeVoyage?.id !== ID_COLLECT_VOYAGE
    ) {
      return {disabled: true, message: "Desembarcar viagem anterior"};
    } else {
      return {disabled: false};
    }
  };

  const shouldDisableUnload = (
    currentVoyageIndex: number,
    voyages: Voyage[],
  ) => {
    /**
     * [] Apenas habilitar caso anterior tenha desembarcado (exceto coleta)
     * [] Apenas habilitar caso embarque atual tenha sido feito
     */
    const currentVoyage = voyages[currentVoyageIndex];

    //Não aplicar bloqueio para coleta, DTA e entrega
    if (
      ![
        ID_COLLECT_VOYAGE,
        ID_DELIVERY_VOYAGE,
        ID_FINAL_DESTINATION_VOYAGE,
        ID_RECEIVING_LOCATION,
      ].includes(currentVoyage?.typeVoyage?.id ?? 0) &&
      !isAiProcess
    ) {
      if (currentVoyage?.cargoUnload === "") {
        return {disabled: true, message: "Desembarcar via atividade"};
      }
    }
    //se a viagem 2 estiver embarcada, desabilitar embarque da v1
    //se existe voyage + 1 && voyage[i+1].
    if (
      voyages[currentVoyageIndex + 1] &&
      voyages[currentVoyageIndex + 1]?.cargoLoad &&
      currentVoyage?.typeVoyage?.id !== ID_COLLECT_VOYAGE
    ) {
      return {disabled: true, message: "Viagem posterior embarcada"};
    }

    if (!currentVoyage?.cargoLoad && !isAiProcess) {
      return {disabled: true, message: "Embarque não realizado"};
    } else {
      return {disabled: false};
    }

    //se desembarque preenchido, desabilitar embarque
  };

  const minDateVoyageLoad = (currentVoyageIndex: number, voyages: Voyage[]) => {
    /**
     * [] Hoje ou anterior
     * [] Sempre posterior ao desembarque da viagem anterior
     */

    // aqui eu posso considerar que o desembarque existe, caso contrário esse campo
    // será desabilitado.
    const previousVoyageIsCollectAndDoesntHaveUnload =
      !voyages[currentVoyageIndex - 1]?.cargoUnload &&
      voyages?.[currentVoyageIndex - 1]?.typeVoyage?.id === ID_COLLECT_VOYAGE;

    if (
      currentVoyageIndex === 0 ||
      previousVoyageIsCollectAndDoesntHaveUnload
    ) {
      //caso primeira viagem do array ou anterior é coleta pode ser qualquer coisa
      return undefined;
    }
    const result = utcToZonedTime(
      add(new Date(voyages?.[currentVoyageIndex - 1]?.cargoUnload ?? ""), {
        hours: 3,
      }),
      timeZone,
    );

    return {
      date: result,
      message: "A data de embarque deve ser posterior ao último desembarque.",
    };
  };

  const minDateVoyageUnload = (
    currentVoyageIndex: number,
    voyages: Voyage[],
  ) => {
    /**
     * [] Sempre posterior ao desembarque da viagem anterior
     * [] Não pode data futura
     */

    // [] Mesmo dia ou posterior ao embarque da mesma viagem
    const result = utcToZonedTime(
      add(new Date(voyages?.[currentVoyageIndex]?.cargoLoad ?? ""), {
        hours: 3,
      }),
      timeZone,
    );

    return {
      date: result,
      message:
        "A data de desembarque deve ser igual ou posterior à data de embarque.",
    };
  };

  const maxDateVoyageLoad = (currentVoyageIndex: number, voyages: Voyage[]) => {
    const nextVoyageHasLoad = voyages?.[currentVoyageIndex + 1]?.cargoLoad;

    let result = brazilianTimeZoneCurrentDate;
    let message = "";
    if (nextVoyageHasLoad) {
      result = utcToZonedTime(
        add(new Date(voyages?.[currentVoyageIndex + 1]?.cargoLoad ?? ""), {
          hours: 3,
        }),
        timeZone,
      );
      message =
        "A data de embarque deve ser anterior ao embarque da viagem seguinte.";
    }

    return {
      date: result,
      message: message,
    };
  };

  const validateLoadMaxDate = (
    value: string | Date,
    index: number,
    voyages: Voyage[],
  ) => {
    const currentLoadDate = new Date(value);
    const nextVoyageLoadDate = new Date(voyages?.[index + 1]?.cargoLoad ?? "");
    const normalizedDate1 = startOfDay(currentLoadDate);
    const normalizedDate2 = startOfDay(nextVoyageLoadDate);

    const isSameOrBeforeNextVoyageLoad =
      isBefore(normalizedDate1, normalizedDate2) ||
      isEqual(normalizedDate1, normalizedDate2);

    /**
     * Válido se:
     * - A data de embarque for anterior ao embarque da viagem seguinte
     * - A viagem seguinte não tem embarque
     * - A viagem é coleta ou local de recebimento
     */
    if (
      isSameOrBeforeNextVoyageLoad ||
      !voyages?.[index + 1]?.cargoLoad ||
      voyages[index]?.typeVoyage?.id === ID_COLLECT_VOYAGE ||
      voyages[index]?.typeVoyage?.id === ID_RECEIVING_LOCATION
    ) {
      return true;
    }
    return `Embarque viagem ${index + 1}`;
  };

  const shouldDisableOrigin = (voyage: Voyage) => {
    if (voyage?.typeVoyage?.id === ID_RECEIVING_LOCATION) {
      return {disabled: false};
    }

    if (voyage?.typeVoyage?.id === ID_COLLECT_VOYAGE) {
      return {disabled: true, message: "Viagem de coleta"};
    }

    if (voyage?.typeVoyage?.id !== ID_INITIAL_VOYAGE) {
      return {disabled: true, message: "Origem igual ao último destino"};
    }

    return {disabled: false};
  };

  const validateDestination = (
    value: {id: string | number; label: string},
    index: number,
    voyages: Voyage[],
  ) => {
    const isTheSameAsCurrentVoyageOrigin =
      voyages?.[index]?.origin?.id === value?.id;
    const isDestinationDuplicated = voyages?.some(
      (item, idx) => idx !== index && item?.destination?.id == value?.id,
    );
    const isTheSameAsOtherVoyageOriginExcludingNextVoyage = voyages?.some(
      (item, idx) =>
        idx !== index && idx !== index + 1 && item?.origin?.id == value?.id,
    );

    if (voyages[index]?.typeVoyage?.id === ID_DELIVERY_VOYAGE) {
      return true;
    }

    if (isTheSameAsCurrentVoyageOrigin) {
      return `Viagem ${index + 1} - Destino igual a origem.`;
    } else if (isDestinationDuplicated) {
      return `Viagem ${index + 1} - Destino duplicado.`;
    } else if (isTheSameAsOtherVoyageOriginExcludingNextVoyage) {
      return `Viagem ${index + 1} - Destino igual a origem de outra viagem.`;
    }
    return true;
  };

  /**
   * Não usar essa função daqui.
   * Ela foi replicada na função shouldUpdateNextVoyageOrigin no arquivo voyageDeletionHelpers.ts
   * O outro hook era mais adequado para esse caso.
   */
  const shouldSetNextVoyageOrigin = (voyage: Voyage, voyages: Voyage[]) => {
    const isCollectVoyage = voyage.typeVoyage?.id === ID_COLLECT_VOYAGE;
    const isLastVoyage = voyages[voyages.length - 1]?.id === voyage.id;

    if (isCollectVoyage || isLastVoyage) {
      return false;
    }

    return true;
  };

  /**
   * Retorna o tipo de erro quando a data estiver fora da janela permitida:
   * acima de 1 ano no futuro ou acima de 6 meses no passado.
   */
  const dateIsFarFromOneYear = (date: string | null) => {
    if (!date) {
      return null;
    }
    const currentDate = new Date(date);

    if (Number.isNaN(currentDate.getTime())) {
      return "invalid";
    }

    const today = startOfDay(new Date());
    const oneYearFromNow = add(today, {years: 1});
    const oneYearBefore = sub(today, {years: 1});

    if (isAfter(currentDate, oneYearFromNow)) {
      return "future";
    }

    if (isBefore(currentDate, oneYearBefore)) {
      return "past";
    }

    return null;
  };

  return {
    shouldDisableLoad,
    shouldDisableUnload,
    minDateVoyageLoad,
    minDateVoyageUnload,
    maxDateVoyageLoad,
    validateLoadMaxDate,
    shouldDisableOrigin,
    validateDestination,
    shouldSetNextVoyageOrigin,
    dateIsFarFromOneYear,
  };
};

export default useValidateVoyages;
